
import { Component, Vue } from 'vue-property-decorator';
import BackgroundImage from '@/models/BackgroundImage';
import BackgroundImageRepository from '@/repositories/BackgroundImageRepository';
import { RouteNames } from '@/enums/routes/RouteNames';
import SettingsTopBar from '@/components/views/settings/SettingsTopBar.vue';

interface UploadForm {
    file: File | null;
    position: number;
}

@Component({
    name: 'LoginImages',
    components: {
        SettingsTopBar,
    },
})
export default class LoginImages extends Vue {
    private backRoute: RouteNames = RouteNames.settings;
    private loading = false;
    private error = '';
    private showUploadDialog = false;
    private showDeleteDialog = false;
    private uploading = false;
    private deleting = false;
    private deletingIds: string[] = [];
    private imageToDelete: BackgroundImage | null = null;

    private uploadForm: UploadForm = {
        file: null,
        position: 1,
    };

    async mounted() {
        await this.loadImages();
    }

    private get backgroundImages() {
        return BackgroundImageRepository.getAll();
    }

    private async loadImages() {
        this.loading = true;
        this.error = '';

        try {
            await BackgroundImage.getAll();
        } catch (error) {
            console.error('Failed to load background images:', error);
            this.error = this.$t('loginImages.loadError').toString();
        } finally {
            this.loading = false;
        }
    }

    private beforeUpload(file: File) {
        this.uploadForm.file = file;
        return false; // Prevent automatic upload
    }

    private async uploadImage() {
        if (!this.uploadForm.file) {
            return;
        }

        this.uploading = true;
        this.error = '';

        try {
            const formData = new FormData();
            formData.append('image', this.uploadForm.file);
            formData.append('position', this.uploadForm.position.toString());

            await BackgroundImage.createNew(formData);
            await this.loadImages();
            this.closeUploadDialog();
            this.resetUploadForm();
        } catch (error) {
            console.error('Failed to upload image:', error);
            this.error = this.$t('loginImages.uploadError').toString();
        } finally {
            this.uploading = false;
        }
    }

    private confirmDelete(image: BackgroundImage) {
        this.imageToDelete = image;
        this.showDeleteDialog = true;
    }

    private async deleteImage() {
        if (!this.imageToDelete) {
            return;
        }

        this.deleting = true;
        this.deletingIds.push(this.imageToDelete.id);
        this.error = '';

        try {
            await BackgroundImage.deleteExisting(this.imageToDelete.id);
            await this.loadImages();
            this.closeDeleteDialog();
        } catch (error) {
            console.error('Failed to delete image:', error);
            this.error = this.$t('loginImages.deleteError').toString();
        } finally {
            this.deleting = false;
            this.deletingIds = this.deletingIds.filter((id) => id !== this.imageToDelete?.id);
        }
    }

    private closeUploadDialog() {
        this.showUploadDialog = false;
        this.resetUploadForm();
    }

    private closeDeleteDialog() {
        this.showDeleteDialog = false;
        this.imageToDelete = null;
    }

    private resetUploadForm() {
        this.uploadForm = {
            file: null,
            position: 1,
        };
    }

    private getImageUrl(image: BackgroundImage): string {
        // Based on the CURL example, images are served from /uploads/images/

        if (image.image) {
            // If the image field contains a full URL, use it
            if (image.image.startsWith('http')) {
                return image.image;
            }
            // If it's a relative path, prepend the base URL
            if (image.image.startsWith('/uploads/')) {
                return image.image;
            }
            // If it's just a filename, construct the full path
            return `/uploads/images/${image.image}`;
        }
        return '';
    }

    private onImageError(event: Event) {
        const img = event.target as HTMLImageElement;
        img.src = '/favicon.ico'; // Fallback image
    }
}
